import React from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface Task {
  id: string;
  title: string;
  description?: string;
  category: string;
  dueDate?: Date;
  completed: boolean;
  isRecurring: boolean;
}

interface CalendarViewProps {
  tasks: Task[];
}

const CalendarView: React.FC<CalendarViewProps> = ({ tasks }) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(new Date());

  const getCategoryStyles = (category: string) => {
    switch (category.toLowerCase()) {
      case 'learning':
        return 'bg-gradient-to-br from-amber-500/90 to-orange-400/90 border-orange-300 text-white';
      case 'personal':
        return 'bg-gradient-to-br from-violet-500/90 to-purple-400/90 border-purple-300 text-white';
      case 'work':
        return 'bg-gradient-to-br from-sky-500/90 to-blue-400/90 border-blue-300 text-white';
      case 'health':
        return 'bg-gradient-to-br from-emerald-500/90 to-green-400/90 border-green-300 text-white';
      case 'shopping':
        return 'bg-gradient-to-br from-teal-500/90 to-cyan-400/90 border-cyan-300 text-white';
      case 'gym':
        return 'bg-gradient-to-br from-rose-500/90 to-pink-400/90 border-pink-300 text-white';
      default:
        return 'bg-gradient-to-br from-gray-500/90 to-slate-400/90 border-slate-300 text-white';
    }
  };

  // Get tasks for the selected date
  const selectedDateTasks = tasks.filter(task => {
    if (!task.dueDate || !selectedDate) return false;
    const taskDate = new Date(task.dueDate);
    return (
      taskDate.getDate() === selectedDate.getDate() &&
      taskDate.getMonth() === selectedDate.getMonth() &&
      taskDate.getFullYear() === selectedDate.getFullYear()
    );
  });

  // Get dates with tasks for highlighting in calendar
  const datesWithTasks = tasks
    .filter(task => task.dueDate)
    .map(task => new Date(task.dueDate!));

  return (
    <div className="flex flex-col items-center gap-8">
      <div className="w-full max-w-[800px] grid gap-8 md:grid-cols-[auto_300px]">
        <Card className="w-full flex justify-center bg-white/80 backdrop-blur-md border border-white/20 shadow-xl">
          <CardContent className="p-0">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md"
              modifiers={{
                withTasks: datesWithTasks,
              }}
              modifiersStyles={{
                withTasks: {
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(var(--primary), 0.05)',
                  color: 'rgb(var(--primary))',
                },
              }}
            />
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="bg-white/80 backdrop-blur-md border border-white/20 shadow-xl">
            <CardContent className="p-4">
              <h3 className="font-extrabold text-lg mb-4">
                Tasks for {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : 'Selected Date'}
              </h3>
              {selectedDateTasks.length > 0 ? (
                <div className="space-y-2">
                  {selectedDateTasks.map((task) => (
                    <div
                      key={task.id}
                      className={cn(
                        "group cursor-pointer rounded-xl p-3 border transition-all duration-300",
                        "hover:shadow-lg hover:scale-[1.02] backdrop-blur-sm",
                        getCategoryStyles(task.category)
                      )}
                      onClick={() => navigate(`/task/${task.id}`)}
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-start justify-between">
                          <h3 className="font-extrabold truncate text-base">
                            {task.title}
                          </h3>
                          {task.isRecurring && (
                            <Badge variant="outline" className="text-[10px] border-white/40 bg-white/10 text-white backdrop-blur-sm font-extrabold px-1 py-0 ml-1.5">
                              ↻
                            </Badge>
                          )}
                        </div>

                        {task.description && (
                          <p className="text-xs font-bold line-clamp-2 text-white/90">
                            {task.description}
                          </p>
                        )}

                        <div className="flex items-center gap-1.5">
                          <Badge className="font-extrabold capitalize text-[10px] px-1.5 py-0 bg-white/20 text-white border-white/10">
                            {task.category}
                          </Badge>
                          <Badge variant="outline" className="font-extrabold text-[10px] px-1.5 py-0 border-white/20 text-white">
                            {new Date(task.dueDate!).toLocaleTimeString(undefined, {
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 font-bold">
                    No tasks scheduled for this date
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CalendarView; 